
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { fetchActiveCampaignsForUser, fetchCampaignQuestions, shouldShowModal, createModalLog, getPreviousAttempts } from '@/services/modalCampaigns';
import { ModalCampaign, ModalCampaignQuestion, ModalCampaignUserLog } from '@/types/modalCampaigns';

export const useActiveCampaigns = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [currentCampaign, setCurrentCampaign] = useState<ModalCampaign | null>(null);
  const [questions, setQuestions] = useState<ModalCampaignQuestion[]>([]);
  const [currentLog, setCurrentLog] = useState<ModalCampaignUserLog | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [attemptNumber, setAttemptNumber] = useState<number>(1);

  useEffect(() => {
    const loadActiveCampaigns = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const campaigns = await fetchActiveCampaignsForUser(user.id);
        
        if (campaigns.length === 0) {
          setLoading(false);
          return;
        }
        
        // Check each campaign for display eligibility based on frequency
        for (const campaign of campaigns) {
          const shouldShow = await shouldShowModal(campaign.id, user.id, campaign.frequency);
          
          if (shouldShow) {
            setCurrentCampaign(campaign);
            
            if (campaign.has_quiz) {
              // Load questions for quiz
              const campaignQuestions = await fetchCampaignQuestions(campaign.id);
              setQuestions(campaignQuestions);
              
              // Get previous attempt count
              const previousAttempts = await getPreviousAttempts(campaign.id, user.id);
              setAttemptNumber(previousAttempts + 1);
            }
            
            // Create log entry for this display
            const log = await createModalLog(campaign.id, user.id);
            setCurrentLog(log);
            
            // Show the modal
            setShowModal(true);
            break;
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading modal campaigns:', error);
        setLoading(false);
      }
    };
    
    loadActiveCampaigns();
    
    // Set up interval to check for new modals (e.g., every 5 minutes)
    const intervalId = setInterval(loadActiveCampaigns, 5 * 60 * 1000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [user]);

  return {
    loading,
    currentCampaign,
    questions,
    showModal,
    currentLog,
    attemptNumber,
    setShowModal,
    setCurrentCampaign,
    setQuestions,
    setCurrentLog,
    setAttemptNumber
  };
};
